import React from 'react';
import LoadingButton, { LoadingButtonProps } from '@mui/lab/LoadingButton';
import { ButtonProps, IconButtonProps } from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import styles from '@/components/atoms/Button/button.module.css';

interface BaseAtomButtonProps {
  children: React.ReactNode;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  loading?: boolean;
  iconOnly?: boolean;
  className?: string;
}

type AtomButtonProps = BaseAtomButtonProps &
  Omit<ButtonProps, keyof BaseAtomButtonProps> &
  Omit<IconButtonProps, keyof BaseAtomButtonProps> &
  Omit<LoadingButtonProps, keyof BaseAtomButtonProps>;

const AtomButton: React.FC<AtomButtonProps> = ({
  children,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  fullWidth = false,
  disabled = false,
  onClick,
  startIcon,
  endIcon,
  loading = false,
  iconOnly = false,
  className,
  ...props
}) => {
  const baseClassName = `${styles.atomButton} ${styles[variant]} ${styles[color]} ${className || ''}`;

  if (iconOnly) {
    return (
      <IconButton
        color={color}
        size={size}
        disabled={disabled}
        onClick={onClick}
        className={`${baseClassName} ${styles.iconButton}`}
        {...props}
      >
        {children}
      </IconButton>
    );
  }

  if (loading) {
    return (
      <LoadingButton
        loading
        variant={variant}
        color={color}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        onClick={onClick}
        startIcon={startIcon}
        endIcon={endIcon}
        className={`${baseClassName} ${loading ? styles.loadingButton : ''}`}
        {...props}
      >
        {children}
      </LoadingButton>
    );
  }

  return (
    <Button
      variant={variant}
      color={color}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      onClick={onClick}
      startIcon={startIcon}
      endIcon={endIcon}
      className={baseClassName}
      {...props}
    >
      {children}
    </Button>
  );
};

export default AtomButton;
